import React from 'react';
import styled, { css } from 'styled-components';

import {
  FaCheck,
  FaCircle,
  FaClock,
  FaCreditCard,
  FaEnvelope,
  FaFire,
  FaHeart,
  FaLocationDot,
  FaMotorcycle,
  FaPhone,
  FaPizzaSlice,
  FaSquareParking,
  FaStar,
  FaTelegram,
  FaTruckFast,
  FaUtensils,
  FaWhatsapp,
} from 'react-icons/fa6';
import { MdShoppingBag } from 'react-icons/md';

import Header from './Header';

import HeroImage from '../assets/png/hero.png';

const HeroSection = styled.section`
  position: relative;

  width: 100%;
  min-height: 600px;

  overflow: hidden;

  background-color: #050505;

  background-image: url(${({ $backgroundImage }) => $backgroundImage}), url(${HeroImage});

  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;

  &::before {
    content: '';

    position: absolute;
    inset: 0;
    z-index: 0;

    pointer-events: none;

    background: linear-gradient(
      90deg,
      rgba(0, 0, 0, ${({ $overlayOpacity }) => Math.min(1, $overlayOpacity + 0.33)}) 0%,
      rgba(0, 0, 0, ${({ $overlayOpacity }) => Math.min(1, $overlayOpacity + 0.26)}) 30%,
      rgba(0, 0, 0, ${({ $overlayOpacity }) => $overlayOpacity * 0.46}) 65%,
      rgba(0, 0, 0, ${({ $overlayOpacity }) => $overlayOpacity * 0.15}) 100%
    );
  }

  @media (max-width: 700px) {
    min-height: 500px;

    background-position: 65% center;

    &::before {
      background: linear-gradient(
        180deg,
        rgba(0, 0, 0, ${({ $overlayOpacity }) => $overlayOpacity}) 0%,
        rgba(0, 0, 0, ${({ $overlayOpacity }) => Math.min(1, $overlayOpacity + 0.27)}) 55%,
        rgba(5, 5, 5, 1) 100%
      );
    }
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 5;

  width: min(100% - 80px, 1500px);

  margin: 0 auto;
  padding: 55px 0 70px;

  @media (max-width: 900px) {
    width: min(100% - 40px, 1500px);
  }

  @media (max-width: 600px) {
    width: min(100% - 30px, 1500px);

    padding-top: 60px;
    padding-bottom: 45px;
  }
`;

const HeroText = styled.div`
  width: 100%;
  max-width: 850px;
  min-width: 0;
`;

const TitleBlock = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  justify-content: space-between;

  gap: 28px;

  min-width: 0;
  min-height: 350px;

  box-sizing: border-box;

  margin-bottom: 25px;

  @media (max-width: 600px) {
    justify-content: flex-start;

    gap: 22px;

    min-height: 0;

    margin-bottom: 20px;
  }
`;

const Title = styled.h1`
  width: 100%;
  max-width: 100%;
  min-width: 0;

  margin: 0;

  font-weight: 950;

  text-transform: uppercase;

  text-shadow: 0 5px 25px rgba(0, 0, 0, 0.5);
`;

const MainTitleGroup = styled.span`
  display: block;

  width: 100%;
  max-width: 100%;
  min-width: 0;

  color: ${({ $textColor }) => $textColor};

  font-family: ${({ $fontFamily }) => $fontFamily};
  font-size: ${({ $fontSize }) => `${$fontSize}px`};

  line-height: 0.95;

  @media (max-width: 900px) {
    font-size: ${({ $mobileFontSize }) => `clamp(32px, 8vw, ${$mobileFontSize}px)`};

    line-height: 1;
  }
`;

const AccentTitleGroup = styled.span`
  display: block;

  width: 100%;
  max-width: 100%;
  min-width: 0;

  margin-top: 15px;

  color: ${({ $textColor }) => $textColor};

  font-family: ${({ $fontFamily }) => $fontFamily};
  font-size: ${({ $fontSize }) => `${$fontSize}px`};

  line-height: 0.95;

  @media (max-width: 900px) {
    font-size: ${({ $mobileFontSize }) => `clamp(32px, 8vw, ${$mobileFontSize}px)`};

    line-height: 1;
  }

  @media (max-width: 600px) {
    margin-top: 12px;
  }
`;

const TitleLine = styled.span`
  display: block;

  width: 100%;
  max-width: 100%;
  min-width: 0;

  white-space: normal;

  overflow-wrap: anywhere;
  word-break: normal;
`;

const YellowLine = styled.div`
  flex-shrink: 0;

  width: 150px;
  height: 4px;

  border-radius: 10px;

  background: ${({ $lineColor }) => $lineColor};

  @media (max-width: 600px) {
    width: 70px;
  }
`;

const LabelsBlock = styled.div`
  width: 100%;
`;

const FeatureList = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  gap: 35px;

  margin: 0 0 30px;

  @media (max-width: 600px) {
    align-items: flex-start;
    flex-direction: column;

    gap: 15px;
  }
`;

const featureStyles = css`
  display: inline-flex;
  align-items: center;

  gap: 12px;

  color: ${({ $textColor }) => $textColor};

  font-size: 15px;
  font-weight: 600;
  line-height: 1.3;

  svg {
    flex-shrink: 0;

    color: ${({ $iconColor }) => $iconColor};

    font-size: 23px;
  }
`;

const Feature = styled.div`
  ${featureStyles}
`;

const FeatureLink = styled.a`
  ${featureStyles}

  border-radius: 6px;

  text-decoration: none;

  transition:
    opacity 0.2s ease,
    transform 0.2s ease;

  &:hover {
    opacity: 0.85;

    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 2px solid ${({ $iconColor }) => $iconColor};
    outline-offset: 5px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }
`;

const fallbackLabels = [
  {
    id: 'fallback-shopping-bag',
    icon: 'shopping_bag',
    text: 'Asporto',
    link_url: '',
    open_in_new_tab: false,
    position: 0,
  },
  {
    id: 'fallback-clock',
    icon: 'clock',
    text: 'Aperto fino alle 23:00',
    link_url: '',
    open_in_new_tab: false,
    position: 1,
  },
];

const fontFamilies = {
  default: 'inherit',
  arial: 'Arial, Helvetica, sans-serif',
  georgia: 'Georgia, "Times New Roman", serif',
  times_new_roman: '"Times New Roman", Times, serif',
  trebuchet: '"Trebuchet MS", Arial, sans-serif',
  impact: 'Impact, Haettenschweiler, "Arial Narrow Bold", sans-serif',
  courier_new: '"Courier New", Courier, monospace',
};

const normalizeOverlayOpacity = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return 0.65;
  }

  return Math.min(1, Math.max(0, numericValue / 100));
};

const normalizeFontSize = (value, fallback = 95) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return fallback;
  }

  return Math.min(140, Math.max(40, numericValue));
};

const calculateMobileFontSize = (desktopFontSize) => {
  return Math.min(64, Math.max(32, Math.round(desktopFontSize * 0.72)));
};

const normalizeTitleLine = (value, fallback) => {
  if (typeof value !== 'string') {
    return fallback;
  }

  const normalizedValue = value.trim();

  return normalizedValue || fallback;
};

const getFontFamily = (fontName) => {
  return fontFamilies[fontName] || fontFamilies.default;
};

const getLabelIcon = (iconName) => {
  const icons = {
    shopping_bag: MdShoppingBag,
    clock: FaClock,
    truck: FaTruckFast,
    phone: FaPhone,
    location: FaLocationDot,
    email: FaEnvelope,
    whatsapp: FaWhatsapp,
    telegram: FaTelegram,
    credit_card: FaCreditCard,
    parking: FaSquareParking,
    utensils: FaUtensils,
    motorcycle: FaMotorcycle,
    pizza: FaPizzaSlice,
    fire: FaFire,
    star: FaStar,
    heart: FaHeart,
    check: FaCheck,
  };

  const IconComponent = icons[iconName] || FaCircle;

  return <IconComponent aria-hidden="true" />;
};

const prepareLabels = (labels) => {
  if (!Array.isArray(labels)) {
    return fallbackLabels;
  }

  if (labels.length === 0) {
    return [];
  }

  return labels
    .filter((label) => label?.text?.trim())
    .sort(
      (firstLabel, secondLabel) =>
        Number(firstLabel.position ?? 0) - Number(secondLabel.position ?? 0),
    );
};

function HeroLabelItem({ label, labelsTextColor, labelsIconColor }) {
  const content = (
    <>
      {getLabelIcon(label.icon)}

      <span>{label.text}</span>
    </>
  );

  if (!label.link_url) {
    return (
      <Feature $textColor={labelsTextColor} $iconColor={labelsIconColor}>
        {content}
      </Feature>
    );
  }

  const shouldOpenInNewTab = Boolean(label.open_in_new_tab);

  return (
    <FeatureLink
      href={label.link_url}
      target={shouldOpenInNewTab ? '_blank' : undefined}
      rel={shouldOpenInNewTab ? 'noopener noreferrer' : undefined}
      aria-label={label.text}
      $textColor={labelsTextColor}
      $iconColor={labelsIconColor}
    >
      {content}
    </FeatureLink>
  );
}

function Hero({ topSettings, headerSettings }) {
  const isActive = headerSettings?.is_active ?? true;

  const backgroundImage = headerSettings?.background_image || HeroImage;

  const overlayOpacity = normalizeOverlayOpacity(headerSettings?.overlay_opacity ?? 65);

  const mainTextLine1 = normalizeTitleLine(headerSettings?.main_text_line_1, 'IL VERO');

  const mainTextLine2 = normalizeTitleLine(headerSettings?.main_text_line_2, 'GUSTO');

  const accentTextLine1 = normalizeTitleLine(headerSettings?.accent_text_line_1, 'OGNI');

  const accentTextLine2 = normalizeTitleLine(headerSettings?.accent_text_line_2, 'GIORNO');

  const mainTextColor = headerSettings?.main_text_color ?? '#ffffff';

  const accentTextColor = headerSettings?.accent_text_color ?? '#df2025';

  const mainFontFamily = getFontFamily(headerSettings?.main_text_font);

  const accentFontFamily = getFontFamily(headerSettings?.accent_text_font);

  const mainFontSize = normalizeFontSize(headerSettings?.main_text_font_size);

  const accentFontSize = normalizeFontSize(headerSettings?.accent_text_font_size);

  const mainMobileFontSize = calculateMobileFontSize(mainFontSize);

  const accentMobileFontSize = calculateMobileFontSize(accentFontSize);

  const decorativeLineColor = headerSettings?.decorative_line_color ?? '#f6c900';

  const labelsTextColor = headerSettings?.labels_text_color ?? '#ffffff';

  const labelsIconColor = headerSettings?.labels_icon_color ?? '#f6c900';

  const labels = prepareLabels(headerSettings?.labels);

  if (!isActive) {
    return null;
  }

  return (
    <HeroSection id="home" $backgroundImage={backgroundImage} $overlayOpacity={overlayOpacity}>
      <Header topSettings={topSettings} />

      <HeroContent>
        <HeroText>
          <TitleBlock>
            <Title>
              <MainTitleGroup
                $textColor={mainTextColor}
                $fontFamily={mainFontFamily}
                $fontSize={mainFontSize}
                $mobileFontSize={mainMobileFontSize}
              >
                <TitleLine>{mainTextLine1}</TitleLine>

                <TitleLine>{mainTextLine2}</TitleLine>
              </MainTitleGroup>

              <AccentTitleGroup
                $textColor={accentTextColor}
                $fontFamily={accentFontFamily}
                $fontSize={accentFontSize}
                $mobileFontSize={accentMobileFontSize}
              >
                <TitleLine>{accentTextLine1}</TitleLine>

                <TitleLine>{accentTextLine2}</TitleLine>
              </AccentTitleGroup>
            </Title>

            <YellowLine $lineColor={decorativeLineColor} />
          </TitleBlock>

          <LabelsBlock>
            <FeatureList>
              {labels.map((label) => (
                <HeroLabelItem
                  key={label.id ?? `${label.icon}-${label.position}-${label.text}`}
                  label={label}
                  labelsTextColor={labelsTextColor}
                  labelsIconColor={labelsIconColor}
                />
              ))}
            </FeatureList>
          </LabelsBlock>
        </HeroText>
      </HeroContent>
    </HeroSection>
  );
}

export default Hero;
